import { primaryRate } from '@/store/crew/crew.mappers';
import {
  CrewPosition,
  type CreateUpdateProfile,
  type CrewRate,
  RateType,
  type SingleCrew,
} from '@/store/crew/crew.types';
import type { Person } from '@/store/persons/person.dto';
import { formatCurrency } from '@/utils/formatCurrency';

export const findPersonById = (people: Person[], id: string) =>
  people?.find((p) => p.id === id) ?? null;

export const positionLabels: Record<CrewPosition, string> = {
  [CrewPosition.CAPTAIN]: 'Captain',
  [CrewPosition.MATE_DECKHAND]: 'Mate / Deckhand',
  [CrewPosition.STEWARD]: 'Steward',
  [CrewPosition.CHEF]: 'Chef',
};

export const positionOptions = Object.entries(positionLabels).map(([value, label]) => ({
  label,
  value,
}));

export const getPositionsDisplayValue = (positions: string[]) => {
  return positionOptions
    .filter(({ value }) => positions.includes(value))
    .map(({ label }) => label)
    .join(', ');
};

export const RATE_PERIOD_OPTIONS: { label: string; value: RateType }[] = [
  { label: 'per hour', value: RateType.HOUR },
  { label: 'per day', value: RateType.DAY },
  { label: 'per month', value: RateType.MONTH },
];

/** "$250 / day", or empty when no rate is stated. */
export const formatRate = (number: number | null | undefined, period: RateType): string =>
  number
    ? `$${formatCurrency(number, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} / ${period}`
    : '';

const NEGOTIABLE_RATE = 'Negotiable';

/** A profile's rate in one line: "Negotiable", or the period it is paid by and what it is. */
export const getRateDisplayValue = (rate: CrewRate | null | undefined): string => {
  if (rate?.negotiable) return NEGOTIABLE_RATE;
  const primary = primaryRate(rate);
  return primary ? formatRate(primary.number, primary.period) : '';
};

/** The CV form's rate in one line, the way the saved profile shows it. */
export const getFormRateDisplayValue = ({
  number,
  period,
  negotiable,
}: CreateUpdateProfile['rate']): string =>
  negotiable ? NEGOTIABLE_RATE : formatRate(number, period);

/**
 * Years of experience as the CV states them, falling back to what the work
 * history adds up to. Empty when neither is known, so the caller can leave the
 * fact out rather than print a zero it cannot stand behind.
 */
export const getExperienceDisplayValue = (
  crew: Pick<SingleCrew, 'yearsOfExperience' | 'yearsOfExperienceCalculated'>,
): string => {
  if (crew.yearsOfExperience) return String(crew.yearsOfExperience);
  if (crew.yearsOfExperienceCalculated) return String(Math.round(crew.yearsOfExperienceCalculated));
  return '';
};

// Parses locale-flexible numeric input.
// "1.000.000,32" → 1000000.32, "1,234.56" → 1234.56, "10.000" → 10000, "10.5" → 10.5, "10,5" → 10.5
export const parseRateNumber = (value: string | number): number => {
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  let s = String(value)
    .trim()
    .replace(/[^\d.,]/g, '');
  if (!s) return 0;

  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let decimalSep: '.' | ',' | null = null;

  if (lastDot !== -1 && lastComma !== -1) {
    decimalSep = lastDot > lastComma ? '.' : ',';
  } else if (lastComma !== -1) {
    decimalSep = ',';
  } else if (lastDot !== -1) {
    // Only dots: 3-digit trailing group = thousand sep, otherwise decimal.
    decimalSep = s.length - lastDot - 1 === 3 ? null : '.';
  }

  if (decimalSep) {
    const thousand = decimalSep === '.' ? ',' : '.';
    s = s.split(thousand).join('');
    if (decimalSep === ',') s = s.replace(',', '.');
  } else {
    s = s.replace(/[.,]/g, '');
  }

  const parsed = parseFloat(s);
  return isNaN(parsed) ? 0 : parsed;
};

export const mapProfileToPersonUpdate = (values: CreateUpdateProfile) => ({
  first_name: values.firstName,
  last_name: values.lastName,
  gender: values.gender,
});

export const licenseOptions = [
  { label: '25 Ton Master USCG', value: '25_ton_master_uscg' },
  { label: '50 Ton Master USCG', value: '50_ton_master_uscg' },
  { label: '50 Ton Master Position USCG', value: '50_ton_master_position_uscg' },
  { label: '100 Ton USCG', value: '100_ton_uscg' },
  { label: '200 Ton USCG', value: '200_ton_uscg' },
  { label: '1600 Ton USCG', value: '1600_ton_uscg' },
  { label: '3000 Ton USCG', value: '3000_ton_uscg' },
  { label: "Able Seaman's Book", value: 'able_seamans_book' },
  { label: 'ABYC', value: 'abyc' },
  { label: 'ABYC Certified', value: 'abyc_certified' },
  { label: 'Bartending Certified', value: 'bartending_certified' },
  { label: 'BRM', value: 'brm' },
  { label: 'Divemaster', value: 'divemaster' },
  { label: 'Drug Consortium', value: 'drug_consortium' },
  { label: 'Dynamic Positioning Certified', value: 'dynamic_positioning_certified' },
  { label: 'First Aid & CPR Certified', value: 'first_aid_and_cpr_certified' },
  { label: 'GMDSS', value: 'gmdss' },
  { label: 'Head Chef Certification', value: 'head_chef_certification' },
  { label: 'ISPS Code Certified', value: 'isps_code_certified' },
  { label: 'IYBA Member', value: 'iyba_member' },
  { label: 'Lifeboat Operations Certified', value: 'lifeboat_operations_certified' },
  { label: 'MCA Chief Mate Unlimited', value: 'mca_chief_mate_unlimited' },
  { label: 'MYBA', value: 'myba' },
  { label: 'OOW', value: 'oow' },
  { label: 'OOW Unlimited', value: 'oow_unlimited' },
  { label: 'Powerboat Level II', value: 'powerboat_level_ii' },
  { label: 'RYA yacht master', value: 'rya_yacht_master' },
  { label: 'Sailing Endorsement', value: 'sailing_endorsement' },
  { label: 'STCW', value: 'stcw' },
  { label: 'STCW Advanced Firefighting', value: 'stcw_advanced_firefighting' },
  { label: 'Towing Endorsement', value: 'towing_endorsement' },
  { label: 'TWIC Card Holder', value: 'twic_card_holder' },
  { label: 'USCG Third Mate Unlimited Tonnage', value: 'uscg_third_mate_unlimited_tonnage' },
];

/** The chip a candidate who takes temporary contracts wears, on the card and the preview. */
export const OPEN_TO_TEMPORARY_LABEL = 'Open to temporary';

/** Stored licence values are opaque slugs; every list shows what the CV builder called them. */
export const certificationLabels = (licence: string[]): string[] =>
  licence.map((value) => licenseOptions.find((option) => option.value === value)?.label ?? value);
